import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import {
  readFreshSelection,
  stampSchoolSelection,
  notifySchoolSelectionUpdated,
} from '../../../../services/schoolSelectionTtl';
import { classSwitchCopyFor } from '../../Common/SchoolShop/schoolSwitchCopy';
import { ArrowRight } from 'lucide-react';
import { Header, Footer } from '../Common';
import SecondaryBanner from '@ajat/design-system/ContentBlocks/SecondaryBanner';
import schoolShopBanner from '../../Common/SchoolShop/schoolShopBanner';
import FomoBlock from '@ajat/design-system/ContentBlocks/FomoBlock';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import Fullwidth from '@ajat/design-system/ContentBlocks/Fullwidth';
import Preloader from '@ajat/design-system/Preloader';
import ShopEmptyState from '../../Common/SchoolShop/ShopEmptyState';
import SchoolSwitchPopup from '../../Common/SchoolShop/SchoolSwitchPopup';
import useWidget from '../../../../hooks/useWidget';
import useSchools from '../../../../hooks/useSchools';
import useSchoolClass from '../../../../hooks/useSchoolClass';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useSchoolVariations from '../../../../hooks/useSchoolVariations';
import ClosedShopMessage from '../../Common/ClosedShop/ClosedShopMessage';
import { mapProductCarouselItems } from '../../../../hooks/widgetMappers';
import { useCart } from '../../../../context/CartContext';
import { pickGroup } from '../../../../services/menuDestination';
import buildGroupedSections from './buildGroupedSections';
import resolveHeaderImages from '../../Common/SchoolShop/headerImages';
import '../../Common/SchoolShop/SchoolShop.css';
import { discountCardFields } from '../../../../services/discountBadge';

// SecondaryBanner photos: the brand's uploaded header images (variations payload
// `menuItemImages`), else the DS default. FI-TODO: FI-specific fallback photos.

const SCHOOL_STORAGE_KEY = 'clsfi_school_selection';
const BRAND_SLUG = 'clsfi';

const variationToCardItem = (variation, schoolSlug, shopSlug, navigate) => {
  const pickColorValues = (list) => (list ?? [])
    .filter((a) => a.slug === 'color')
    .flatMap((a) => a.values.map((v) => v.rawValue ?? v.value));
  const pinnedColors = pickColorValues(variation.pinned_attributes);
  const optionColors = pickColorValues(variation.available_options);
  const imageList = Array.isArray(variation.images) ? variation.images : [];
  const firstImage = imageList[0]?.src;
  const secondImage = imageList[1]?.src;
  return {
    id: variation.id,
    image: firstImage,
    imageAlt: variation.name,
    secondImage,
    secondImageAlt: variation.name,
    title: variation.name,
    price: variation.price,
    ...discountCardFields(variation, 'cls-fi'),
    colors: pinnedColors.length ? pinnedColors : optionColors,
    // Open the variation (with its shop/class overrides applied), not the bare
    // product, so the configurator prices match the school shop. Carry the shop
    // slug so the exact shop's overrides resolve.
    onClick: () => navigate(
      `/clothing/cls-fi/school-shop/${schoolSlug}/product/${variation.id}`
      + (shopSlug ? `?shop=${encodeURIComponent(shopSlug)}` : '')
    ),
  };
};

// A package card for the bottom "Packages" grid: the package's image, name and
// price. Clicking opens the package configurator on the school-scoped route
// (mirrors product links) so the URL itself carries the school + shop context.
const packageToCardItem = (pkg, schoolSlug, shopSlug, navigate) => ({
  id: `pkg-${pkg.id}`,
  image: pkg.image || undefined,
  imageAlt: pkg.name,
  title: pkg.name,
  description: 'Pakettiratkaisu',
  price: pkg.price,
  onClick: () => navigate(
    schoolSlug
      ? `/clothing/cls-fi/school-shop/${schoolSlug}/product-package/${encodeURIComponent(pkg.slug)}`
        + (shopSlug ? `?shop=${encodeURIComponent(shopSlug)}` : '')
      : `/clothing/cls-fi/product-package/${encodeURIComponent(pkg.slug)}`
  ),
});

/* Persist the selection derived from the URL (school + shop slugs + the ?class=
 * preselect). Stamped so the 24h TTL readers keep it — an unstamped write is
 * deleted by the very next readFreshSelection — and notified so same-tab
 * consumers (useSchoolClass / useSchoolSelection) re-read immediately. */
const persistSelection = (match, matchedShop, klass, fallbackSeason = null) => {
  try {
    localStorage.setItem(SCHOOL_STORAGE_KEY, JSON.stringify(stampSchoolSelection({
      school: {
        id: match.id,
        slug: match.slug,
        name: match.name,
        classes: match.classes,
        seasons: match.seasons,
      },
      season: matchedShop
        ? { id: matchedShop.id, slug: matchedShop.slug, name: matchedShop.name }
        : (fallbackSeason ?? null),
      class: klass ?? null,
    })));
    notifySchoolSelectionUpdated(SCHOOL_STORAGE_KEY);
  } catch { /* ignore */ }
};

/* Match a ?class= value against the classes the shop actually offers (the API
 * exposes only the pivot-enabled ones per shop), falling back to the school's
 * list when the URL carries no shop. An unknown / renamed / disabled class is
 * ignored, so the link simply degrades to a plain shop link. */
const resolveUrlClass = (raw, match, matchedShop) => {
  const wanted = (raw || '').trim();
  if (!wanted) return null;
  const options = (matchedShop?.classes?.length ? matchedShop.classes : match?.classes) || [];
  return options.find((c) => c === wanted)
    ?? options.find((c) => String(c).trim().toLowerCase() === wanted.toLowerCase())
    ?? null;
};

// A "shop selector" card: the shop's name + a representative product image,
// no price or colors. Clicking opens that shop's combined product list.
const shopToCardItem = (shop, schoolSlug, navigate) => ({
  id: `shop-${shop.id}`,
  image: shop.image || undefined,
  imageAlt: shop.name,
  title: shop.name,
  onClick: () => navigate(`/clothing/cls-fi/school-shop/${schoolSlug}/${encodeURIComponent(shop.slug)}`),
});

const SchoolShop = () => {
  const navigate = useNavigate();
  const { schoolSlug, shopSlug, groupSlug } = useParams();
  const [searchParams] = useSearchParams();
  // Class-shop link: ?class={name} preselects the class (admin generates these
  // per class in the shop's class grid / class settings popup). Kept in the URL
  // so the link re-applies the class on every visit — the stored selection
  // itself expires after 24h.
  const urlClassParam = searchParams.get('class');
  const { schools } = useSchools('clsfi');
  const { setSchool: setCartSchool, school: cartSchool, clearCart, itemCount } = useCart(BRAND_SLUG);
  // Cross-school guard: landing here with items from ANOTHER school in the
  // cart requires an explicit "erase & continue" before anything is touched.
  const [schoolConflict, setSchoolConflict] = useState(null);
  // Same guard for a class-shop link that targets another class than the one
  // the cart is bound to (the BE rotates the cart on any class change).
  const [classConflict, setClassConflict] = useState(null);
  // A class the customer declined in that popup — kept so the effect doesn't
  // immediately re-open it (cartSchool.class / itemCount are effect deps).
  const declinedUrlClassRef = useRef(null);
  const { data: productListWidget, loading: productListLoading } = useWidget('clsfi', 'product-list-1');
  const { data: packagesWidget, loading: packagesLoading } = useWidget('clsfi', 'packages-1');
  const productListItems = mapProductCarouselItems(productListWidget, {
    navigate,
    productBasePath: '/clothing/cls-fi/product-details',
    packageBasePath: '/clothing/cls-fi/product-package',
    schoolStorageKey: SCHOOL_STORAGE_KEY,
  });
  const packageItems = mapProductCarouselItems(packagesWidget, {
    navigate,
    productBasePath: '/clothing/cls-fi/product-details',
    packageBasePath: '/clothing/cls-fi/product-package',
    schoolStorageKey: SCHOOL_STORAGE_KEY,
  });

  const className = useSchoolClass(BRAND_SLUG);
  const { school: shopSchool, shop: currentShop, group: currentGroup, collections, menuItemImages, variations, packages: schoolPackages, shops, layout, nextShopClosesAt, paymentDeadline, nextShopClosesAtDisplay, paymentDeadlineDisplay, shopClosed, loading: variationsLoading, error: variationsError } =
    useSchoolVariations(schoolSlug ? BRAND_SLUG : null, schoolSlug, className, shopSlug, groupSlug);
  // School/shop-scoped packages take precedence over the global widget on the
  // school shop pages; fall back to the global widget on the brand landing.
  const schoolPackageItems = useMemo(
    () => (schoolPackages || []).map((p) => packageToCardItem(p, schoolSlug, shopSlug, navigate)),
    [schoolPackages, schoolSlug, shopSlug, navigate],
  );
  const bottomPackageItems = schoolSlug ? schoolPackageItems : packageItems;
  const schoolItems = useMemo(
    () => variations.map((v) => variationToCardItem(v, schoolSlug, shopSlug, navigate)),
    [variations, schoolSlug, shopSlug, navigate],
  );
  // Shop-selector mode: only on the whole-school root view (no shop chosen yet).
  const isShopSelector = !shopSlug && layout === 'shop_selector';
  const shopSelectorItems = useMemo(
    () => shops.map((s) => shopToCardItem(s, schoolSlug, navigate)),
    [shops, schoolSlug, navigate],
  );
  const gridItems = isShopSelector ? shopSelectorItems : schoolItems;
  // GROUPED layout (Figma 8221:42509), data-driven: effective layout = the
  // group's override → the shop's layout → simple (the BE already resolves
  // `group.layout` / `shop.layout`). Sections come from the payload's
  // `collections[]` (root = every collection in group order, group page = the
  // group's own) with the LIVE variation cards, so every product the shop
  // sells is reachable. Shop-selector mode always keeps the flat shop-card grid.
  const effectiveLayout = currentGroup?.layout ?? currentShop?.layout ?? 'simple';
  const grouped = effectiveLayout === 'grouped' && !isShopSelector;
  const groupedSections = useMemo(() => {
    const cardsById = Object.fromEntries(schoolItems.map((c) => [c.id, c]));
    return buildGroupedSections(collections, cardsById);
  }, [collections, schoolItems]);
  const gridEmpty = grouped ? groupedSections.length === 0 : gridItems.length === 0;


  /* School-root URL + exactly ONE open shop → hop to the shop-slugged URL
   * (replace:true). The cart then binds that shop, so shop-scoped context —
   * e.g. the invoice-fee override shown in checkout — matches what checkout
   * charges, and both URL forms behave identically. Multi-shop schools keep
   * the root view. The query string (?class= links) is preserved. */
  useEffect(() => {
    if (!schoolSlug || shopSlug || !schools?.length) return;
    const match = schools.find((s) => s.slug === schoolSlug);
    const openShops = match?.seasons || [];
    if (openShops.length === 1 && openShops[0]?.slug) {
      // Straight into the shop's first group when it has groups (the root is
      // never a link target on a grouped shop), else the shop root.
      const entryGroup = pickGroup(openShops[0]);
      navigate(
        `/clothing/cls-fi/school-shop/${schoolSlug}/${encodeURIComponent(openShops[0].slug)}${entryGroup ? `/${encodeURIComponent(entryGroup.slug)}` : ''}${window.location.search}`,
        { replace: true },
      );
    }
  }, [schoolSlug, shopSlug, schools, navigate]);

  /* Auto-persist school selection from URL slug and keep the BE cart in
   * sync — the cart's school (and, when on a shop-specific URL, the shop) is
   * what ends up on the order. */
  useEffect(() => {
    if (!schoolSlug || !schools?.length) return;
    const match = schools.find((s) => s.slug === schoolSlug);
    if (!match) return;
    // Resolve the shop id from the URL shop slug against this school's shops.
    const matchedShop = shopSlug
      ? (match.seasons || []).find((s) => s.slug === shopSlug)
      : null;
    let prev = null;
    try { prev = JSON.parse(localStorage.getItem(SCHOOL_STORAGE_KEY) || 'null'); } catch { prev = null; }
    const sameSchoolStored = prev?.school?.id === match.id;
    const storedClass = sameSchoolStored ? (prev?.class ?? null) : null;
    // Class-shop link: the URL's class wins over the stored one (unless the
    // customer just declined it in the cart-guard popup).
    const urlClass = resolveUrlClass(urlClassParam, match, matchedShop);
    const linkClass = (urlClass && urlClass !== declinedUrlClassRef.current) ? urlClass : null;
    const targetClass = linkClass ?? storedClass;
    // Only re-bind the cart when the target school or class actually differs
    // from what the cart already holds. A bare remount (e.g. returning from a
    // product page to the SAME shop) must NOT re-issue setSchool: the BE can
    // treat an identical re-bind as a change under some timing (schools not yet
    // loaded → shop resolves null) and rotate the cart to a fresh empty one,
    // losing items. Mirrors ABC-SE's school-sync gating.
    // Coerce ids before comparing: the schools API serializes ids as STRINGS
    // while the cart API returns NUMBERS — a strict mismatch here made every
    // shop visit look like a school change once the cart had a school bound,
    // so clearCart() wiped the items on returning from a product page.
    const cartSchoolId = cartSchool?.id != null ? String(cartSchool.id) : null;
    const urlSchoolId = String(match.id);
    const isSchoolChange = cartSchoolId != null && cartSchoolId !== urlSchoolId;
    // Different school + items in the cart → don't touch anything (storage or
    // cart) until the customer confirms via the SchoolSwitchPopup.
    if (isSchoolChange && itemCount > 0) {
      setSchoolConflict((cur) => cur ?? { match, matchedShop, targetClass });
      return;
    }
    // Same school, but a class link pointing at ANOTHER class than the cart is
    // bound to: the BE rotates the cart to a fresh empty one on a class change,
    // so ask before touching anything.
    const cartClass = cartSchool?.class ?? null;
    if (!isSchoolChange && linkClass && cartSchoolId != null && linkClass !== cartClass && itemCount > 0) {
      setClassConflict((cur) => cur ?? {
        match,
        matchedShop,
        targetClass: linkClass,
        prevSeason: sameSchoolStored ? (prev?.season ?? null) : null,
      });
      return;
    }
    if (!sameSchoolStored || targetClass !== (prev?.class ?? null)) {
      persistSelection(match, matchedShop, targetClass, sameSchoolStored ? prev?.season : null);
    }
    const classChanged = cartClass !== (targetClass ?? null);
    if (cartSchoolId !== urlSchoolId || classChanged) {
      (async () => {
        if (isSchoolChange) {
          try { await clearCart(); } catch { /* best-effort */ }
        }
        try {
          await setCartSchool({
            school_id: match.id,
            shop_id: matchedShop?.id ? Number(matchedShop.id) : null,
            school_class: targetClass,
          });
        } catch { /* cart sync is best-effort */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolSlug, shopSlug, urlClassParam, schools, cartSchool?.id, cartSchool?.class, itemCount]);

  /* A shop link WITHOUT a class: class overrides (prices, deadlines,
   * disabled options) only apply once a class is chosen, so brands with the
   * school-login popup force the choice on landing — the selector opens with
   * the school/shop preselected (requireClass makes the class mandatory).
   * Once per page visit; a ?class= link is handled by the effect above. */
  const { showSchoolSelector, deadlineDisplay } = useBrandSettings(BRAND_SLUG);
  const promptedClassRef = useRef(false);
  useEffect(() => {
    if (!showSchoolSelector || promptedClassRef.current) return;
    if (!schoolSlug || !schools?.length) return;
    if (schoolConflict || classConflict) return;
    if (className || urlClassParam) return;
    const match = schools.find((s) => s.slug === schoolSlug);
    if (!match) return;
    const classes = shopSlug
      ? ((match.seasons || []).find((s) => s.slug === shopSlug)?.classes || [])
      : (match.seasons || []).flatMap((s) => s.classes || []);
    if (!classes.length) return; // class-less shop — nothing to force
    promptedClassRef.current = true;
    document.dispatchEvent(new CustomEvent('open-school-selector'));
  }, [showSchoolSelector, schoolSlug, shopSlug, urlClassParam, schools, className, schoolConflict, classConflict]);

  /* Single-item shop: skip the grid and open the only product's (or only
   * package's) configurator directly — covers direct links, menu links and
   * the cart's "back to shop" (e.g. Vogntrøjer shops carry exactly one
   * product). replace:true keeps the shop page out of history so the
   * configurator's back arrow doesn't bounce straight back here. Every
   * guard the grid enforces must win over the redirect: schools loaded
   * (the selection-persist effect above must have run before we leave),
   * no cross-school/class cart popup pending — mirrored as predicates
   * because those effects set their popup state in the same commit this
   * effect first runs in — and the forced class choice already made. */
  useEffect(() => {
    if (!schoolSlug || variationsLoading || variationsError || isShopSelector) return;
    if (!schools?.length) return;
    const match = schools.find((s) => s.slug === schoolSlug);
    if (!match) return;
    // A single-open-shop school on the root URL is about to hop to the
    // shop-slugged URL (effect above) — let that redirect carry the shop
    // param instead of navigating without it.
    if (!shopSlug && (match.seasons || []).length === 1) return;
    if (schoolConflict || classConflict) return;
    if (itemCount > 0 && cartSchool?.id != null && String(cartSchool.id) !== String(match.id)) return;
    if (itemCount > 0 && urlClassParam && (cartSchool?.class ?? null) !== urlClassParam) return;
    if (showSchoolSelector && !className && !urlClassParam) {
      const classes = shopSlug
        ? ((match.seasons || []).find((s) => s.slug === shopSlug)?.classes || [])
        : (match.seasons || []).flatMap((s) => s.classes || []);
      if (classes.length) return; // class popup owns this visit
    }
    const pkgs = schoolPackages || [];
    const q = shopSlug ? `?shop=${encodeURIComponent(shopSlug)}` : '';
    if (variations.length === 1 && pkgs.length === 0) {
      navigate(`/clothing/cls-fi/school-shop/${schoolSlug}/product/${variations[0].id}${q}`, { replace: true });
    } else if (variations.length === 0 && pkgs.length === 1 && pkgs[0]?.slug) {
      navigate(`/clothing/cls-fi/school-shop/${schoolSlug}/product-package/${encodeURIComponent(pkgs[0].slug)}${q}`, { replace: true });
    }
  }, [schoolSlug, shopSlug, urlClassParam, schools, variationsLoading, variationsError, isShopSelector,
      variations, schoolPackages, itemCount, cartSchool?.id, cartSchool?.class, schoolConflict,
      classConflict, showSchoolSelector, className, navigate]);

  /* Cross-school guard resolution: "Back" returns to the shop the cart
   * belongs to; the red confirm erases the cart and continues here. */
  const handleSwitchBack = useCallback(() => {
    setSchoolConflict(null);
    navigate(cartSchool?.slug
      ? `/clothing/cls-fi/school-shop/${cartSchool.slug}`
      : '/clothing/cls-fi');
  }, [navigate, cartSchool?.slug]);

  const handleSwitchConfirm = useCallback(async () => {
    if (!schoolConflict) return;
    const { match, matchedShop, targetClass } = schoolConflict;
    persistSelection(match, matchedShop, targetClass);
    try { await clearCart(); } catch { /* best-effort */ }
    try {
      await setCartSchool({
        school_id: match.id,
        shop_id: matchedShop?.id ? Number(matchedShop.id) : null,
        school_class: targetClass ?? null,
      });
    } catch { /* cart sync is best-effort */ }
    setSchoolConflict(null);
  }, [schoolConflict, clearCart, setCartSchool]);

  /* Class-link guard resolution: "keep the class" dismisses the popup and
   * remembers the declined class so it isn't re-proposed on every re-render;
   * the red confirm erases the cart and switches to the link's class. */
  const handleClassSwitchBack = useCallback(() => {
    declinedUrlClassRef.current = classConflict?.targetClass ?? null;
    setClassConflict(null);
  }, [classConflict]);

  const handleClassSwitchConfirm = useCallback(async () => {
    if (!classConflict) return;
    const { match, matchedShop, targetClass, prevSeason } = classConflict;
    persistSelection(match, matchedShop, targetClass, prevSeason);
    try { await clearCart(); } catch { /* best-effort */ }
    try {
      await setCartSchool({
        school_id: match.id,
        shop_id: matchedShop?.id ? Number(matchedShop.id) : null,
        school_class: targetClass,
      });
    } catch { /* cart sync is best-effort */ }
    setClassConflict(null);
  }, [classConflict, clearCart, setCartSchool]);

  /* Set global brand on mount, restore previous on unmount */
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'CLS');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  // The bare /school-shop (no school in the URL) is a redundant widget grid —
  // e.g. where an order-cancel used to land. Bounce to the stored school's shop
  // when we know it, else the brand landing.
  if (!schoolSlug) {
    const sel = readFreshSelection(SCHOOL_STORAGE_KEY);
    const storedSchoolSlug = sel?.school?.slug;
    const storedShopSlug = sel?.season?.slug;
    // The stored shop's first group when the persisted school carries groups
    // (school.seasons[].groups); otherwise the shop URL — the single-open-shop
    // hop above re-resolves the group from the live schools list.
    const storedSeason = (sel?.school?.seasons || []).find((s) => s?.slug === storedShopSlug) ?? sel?.season;
    const storedGroup = pickGroup(storedSeason);
    return storedSchoolSlug
      ? <Navigate replace to={`/clothing/cls-fi/school-shop/${storedSchoolSlug}${storedShopSlug ? `/${storedShopSlug}` : ''}${storedShopSlug && storedGroup ? `/${storedGroup.slug}` : ''}`} />
      : <Navigate replace to="/clothing/cls-fi" />;
  }

  const { tag: bannerTag, title: bannerTitle, menuItemKey: bannerMenuKey } = schoolShopBanner({
    schools, schoolSlug, shopSlug, currentShop, currentGroup, school: shopSchool, className,
  });
  // Uploaded per-menu-item photos win, then the static map, then the DS default.
  const bannerImages = resolveHeaderImages({ menuItemImages, menuItemKey: bannerMenuKey });

  // The requested shop's date window has passed (BE `shopClosed` marker):
  // the direct URL renders the localized closed page instead of products.
  // Unknown / removed group slug (BE 404 on ?group=): fall back to the shop
  // root rather than showing an error page for a stale link.
  if (groupSlug && shopSlug && schoolSlug && !variationsLoading && variationsError) {
    return <Navigate replace to={`/clothing/cls-fi/school-shop/${schoolSlug}/${shopSlug}`} />;
  }

  if (shopClosed) {
    return (
      <>
        <Header />
        <ClosedShopMessage brandSlug="clsfi" feSlug="cls-fi" standalone />
        <Footer />
      </>
    );
  }

  return (
    <div className="school-shop">
      <Header />

      <SecondaryBanner
        tag={bannerTag}
        title={bannerTitle}
        trustpilot={null}
        {...bannerImages}
      />

      <main className="school-shop__wrapper">
        {/* Countdown target: payment deadline (class override > shop) wins
            over the shop close date when both are set. */}
        {deadlineDisplay !== 'none' && (paymentDeadline || nextShopClosesAt) && (
          <div className="school-shop__fomo-container">
            <FomoBlock
              closesAt={paymentDeadline || nextShopClosesAt}
              template={deadlineDisplay === 'date'
                ? (paymentDeadline
                  ? 'Maksuaika päättyy {time}'
                  : 'Verkkokauppa sulkeutuu {time}')
                : (paymentDeadline
                  ? 'Kiirehdi! Maksuaika päättyy {time} kuluttua'
                  : 'Kiirehdi! Verkkokauppa sulkeutuu {time} kuluttua')}
              units={{ day: 'pv', hour: 't', minute: 'min' }}
              staticTime={deadlineDisplay === 'date'
                ? (paymentDeadline ? paymentDeadlineDisplay : nextShopClosesAtDisplay) || undefined
                : undefined}
            />
          </div>
        )}

        {/* ── Products Widget 1: Our Collection ── */}
        {schoolSlug ? (
          (variationsLoading || variationsError || gridEmpty) ? (
            variationsLoading ? (
              <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                <Preloader label="Ladataan tuotteita…" />
              </div>
            ) : variationsError ? (
              <ShopEmptyState
                variant="error"
                feSlug="cls-fi"
                title="Tuotteiden lataaminen epäonnistui"
                text="Yritä ladata sivu uudelleen. Jos ongelma jatkuu, autamme mielellämme — katso FAQ tai ota meihin yhteyttä."
                contactLabel="Ota yhteyttä"
                faqLabel="FAQ"
              />
            ) : (
              <ShopEmptyState
                feSlug="cls-fi"
                title="Ei tuotteita juuri nyt"
                text="Kauppa ei ehkä ole vielä auki tai tuotteita päivitetään. Jos sinulla on kysyttävää, autamme mielellämme."
                contactLabel="Ota yhteyttä"
                faqLabel="FAQ"
              />
            )
          ) : grouped ? (
            groupedSections.map((group) => (
              <ProductsGrid
                key={group.id}
                caption={group.caption}
                captionVariant={group.captionVariant}
                title={group.title}
                align="left"
                moreLabel="lisää"
                paddingTop="large-s"
                paddingBottom="large-s"
                background="var(--color-gray-0)"
                className="school-shop__group"
                items={group.items}
              />
            ))
          ) : (
            <ProductsGrid
              moreLabel="lisää"
              paddingTop="medium-l"
              paddingBottom="medium-s"
              background="var(--color-gray-0)"
              ctaIcon={<ArrowRight size={20} />}
              ctaOnClick={() => document.dispatchEvent(new CustomEvent('open-school-selector'))}
              className={isShopSelector ? 'school-shop__shop-cards' : undefined}
              items={gridItems}
            />
          )
        ) : (
          !productListLoading && productListItems && (
            <ProductsGrid
              moreLabel="lisää"
              paddingTop="medium-l"
              paddingBottom="medium-s"
              background="var(--color-gray-0)"
              ctaIcon={<ArrowRight size={20} />}
              ctaOnClick={() => document.dispatchEvent(new CustomEvent('open-school-selector'))}
              items={productListItems}
            />
          )
        )}

        {/* ── Fullwidth Content Widget 1 (grouped layout only) ── */}
        {grouped && (
        <Fullwidth
          showImage={false}
          subtitle="+ 75 000 ASIAKASTA VUOSITTAIN"
          mainCopyVariant="h2"
          mainCopy="Suomen terävimmät hinnat hintatakuulla, nopea toimitus ja houkuttelevat pakettiratkaisut."
          additionalCopy="Tilaa turvallisesti yli 100 vuoden kokemuksella – ja saat ilmaisen paitapaketin edustajille."
          // tags={['A4 selkäpainatus', '10 X 10 cm rintalogo', 'Ilmainen toimitus', 'Helppo yksilöllinen maksu', 'Vapaa tuotevalinta']}
          showHyperlink={false}
        />
        )}

        {/* ── Products Widget 2: Packages (grouped layout only) ── */}
        {grouped && (schoolSlug ? !variationsLoading : !packagesLoading) && bottomPackageItems && bottomPackageItems.length > 0 && (
        <ProductsGrid
          caption="Säästä jopa 20 %"
          captionVariant="success"
          title="Paketit"
          align="left"
          className="school-shop__group"
          moreLabel="lisää"
          background="var(--color-gray-0)"
          paddingTop="medium-m"
          paddingBottom="large-l"
          ctaOnClick={() => document.dispatchEvent(new CustomEvent('open-school-selector'))}
          items={bottomPackageItems}
        />
        )}
      </main>

      <SchoolSwitchPopup
        isOpen={!!schoolConflict}
        brandSlug={BRAND_SLUG}
        onBack={handleSwitchBack}
        onConfirm={handleSwitchConfirm}
      />

      <SchoolSwitchPopup
        isOpen={!schoolConflict && !!classConflict}
        brandSlug={BRAND_SLUG}
        copy={classSwitchCopyFor(BRAND_SLUG)}
        onBack={handleClassSwitchBack}
        onConfirm={handleClassSwitchConfirm}
      />

      <Footer />
    </div>
  );
};

export default SchoolShop;
